import { FaRegClock } from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  InputGroup,
  Placeholder,
} from "react-bootstrap";
import { rsvpSchema } from "../validators/validatorSchema";
import { clearError, validateData } from "../validators/YupValidations";
import SelectField from "../components/common/SelectField";
import { useSelector } from "react-redux";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import { toastErrorMessage, toastSuccessMessage } from "../utils/utils";
import { useNavigate } from "react-router-dom";
import { formatToUSDateTime } from "../utils/dateFormat";
import { formatUSPhone, normalizePhoneForSave } from "../utils/phoneFormat";
import { formatEventDate } from "../utils/formatEventDate";

const Rsvp = () => {
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.USER_DATA);

  console.log(user);

  const IMG_PATH = process.env.REACT_APP_IMAGE_BASEURL || "";
  // console.log("user", user)

  const [formData, setFormData] = useState({
    event: "",
    full_name: "",
    email: "",
    countryCode: "+1",
    phone: "",
    number_of_guests: 1,
  });
  const [events, setEvents] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [errors, setErrors] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [eventDetailsLoading, setEventDetailsLoading] = useState(false);

  const countryCodeOptions = [
    { value: "+1", label: "+1" },
    { value: "+91", label: "+91" },
  ];

  const eventOptions = events.map((item) => ({
    value: item._id,
    label: item.title,
  }));

  useEffect(() => {
    // console.log("Setting user data in RSVP form:", user);
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email || "",
        full_name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        phone:
          user.country_code === "+1"
            ? formatUSPhone(user.phone || "")
            : user.phone || "",
        countryCode: user.country_code,
      }));
    }
  }, [user]);

  const handleDecrement = () => {
    setFormData((prev) => ({
      ...prev,
      number_of_guests: Math.max(1, prev.number_of_guests - 1),
    }));
    errors.number_of_guests && clearError(setErrors, "number_of_guests");
  };

  const handleIncrement = () => {
    setFormData((prev) => ({
      ...prev,
      number_of_guests: prev.number_of_guests + 1,
    }));
    errors.number_of_guests && clearError(setErrors, "number_of_guests");
  };
  const formForValidation = {
    ...formData,
    phone:
      formData.countryCode === "+1"
        ? normalizePhoneForSave(formData.phone)
        : formData.phone.replace(/\D/g, ""),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateData(
      rsvpSchema,
      formForValidation,
      setErrors
    );
    if (!isValid) return;

    try {
      setSubmitting(true);
      const obj = {
        event_id: formData.event,
        first_name: formData.full_name.split(" ")[0] || "",
        last_name: formData.full_name.split(" ")[1] || "",
        email: formData.email,
        phone: formForValidation(formData.phone),
        country_code: formData.countryCode,
        total_persons: formData.number_of_guests,
        user_id: user?._id || "",
      };

      console.log("Submitting RSVP with data:", obj);

      const response = await API.post(api_paths.member.EVENTS.ADD_RSVP, obj);

      console.log("RSVP Response:", response.data);

      toastSuccessMessage(response?.data?.message);

      setSubmitting(false);
      navigate("/");
    } catch (error) {
      toastErrorMessage("Failed to submit RSVP. Please try again.");
      setSubmitting(false);
      // console.error("Error submitting RSVP:", error);
    } finally {
      setSubmitting(false);
    }

    // console.log("RSVP form submitted with valid data.");
  };

  useEffect(() => {
    const fetchAvailableEvents = async () => {
      try {
        const response = await API.get(api_paths.member.EVENTS.GET_LIST);
        // console.log("Events List:", response.data);

        const eventList = response.data?.payload?.result || [];
        setEvents(eventList);

        if (eventList.length > 0) {
          setFormData((prev) => ({
            ...prev,
            event: eventList[0]._id,
          }));
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    // console.log("User Data: ", user);

    fetchAvailableEvents();
  }, []);

  useEffect(() => {
    if (!formData.event) return;

    const fetchEvent = async (eventId) => {
      try {
        setEventDetailsLoading(true);
        const response = await API.get(
          `${api_paths.member.EVENTS.EVENT}/${eventId}`
        );
        // console.log("Event Details:", response.data);

        const eventData = response.data?.payload?.result || null;
        setEventDetails(eventData);
        // console.log("Event data ha been set to: ", eventData);
        setEventDetailsLoading(false);
      } catch (error) {
        // console.error("Error fetching event details:", error);
        setEventDetailsLoading(false);
      }
    };

    fetchEvent(formData.event);
  }, [formData.event]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      setFormData({
        event: "",
        full_name: "",
        email: "",
        countryCode: "+1",
        phone: "",
        number_of_guests: 1,
      });

      setErrors({});
    };
  }, []);

  return (
    <div>
      <Container className="py-5">
        <Row className="mb-4">
          <Col lg={12}>
            <div className="rsvp-header d-flex flex-column justify-content-center align-items-center text-center">
              <h1 className="inter-bold h1">Event RSVP</h1>
              <p>Register for upcoming community events and celebrations</p>
            </div>
          </Col>
        </Row>

        {/* Two-column section */}
        <Row className="gy-4">
          {/* LEFT: Form */}
          <Col lg={6}>
            <Card className="rsvp-form p-3">
              <Card.Body>
                <Card.Title className="fw-bold">
                  Register Your Attendance
                </Card.Title>
                <Card.Text>
                  Fill out the form below to confirm your attendance at an
                  upcoming event
                </Card.Text>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="formEvent">
                    <SelectField
                      label="Select Event"
                      required
                      value={
                        eventOptions.find(
                          (opt) => opt.value === formData.event
                        ) || null
                      }
                      options={eventOptions}
                      onChange={(opt) => {
                        setFormData({
                          ...formData,
                          event: opt ? opt.value : "",
                        });
                        errors.event && clearError(setErrors, "event");
                      }}
                      error={errors.event?.message}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formFullName">
                    <Form.Label>Full Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={(e) => {
                        setFormData({ ...formData, full_name: e.target.value });
                        errors.full_name && clearError(setErrors, "full_name");
                      }}
                    />
                    {errors.full_name && (
                      <Form.Text className="text-danger">
                        {errors.full_name.message}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Email Address *</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        errors.email && clearError(setErrors, "email");
                      }}
                    />
                    {errors.email && (
                      <Form.Text className="text-danger">
                        {errors.email.message}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="formPhone">
                    <Form.Label>Phone Number *</Form.Label>

                    <Row className="g-2">
                      <Col xs={4} md={3}>
                        <SelectField
                          value={countryCodeOptions.find(
                            (opt) => opt.value === formData.countryCode
                          )}
                          options={countryCodeOptions}
                          onChange={(opt) => {
                            const newCode = opt.value;

                            setFormData((prev) => ({
                              ...prev,
                              countryCode: newCode,
                              phone:
                                newCode === "+1"
                                  ? formatUSPhone(
                                      normalizePhoneForSave(prev.phone)
                                    )
                                  : prev.phone.replace(/\D/g, ""),
                            }));

                            errors.phone && clearError(setErrors, "phone");
                          }}
                        />
                      </Col>

                      <Col xs={8} md={9}>
                        <Form.Control
                          type="tel"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={(e) => {
                            let value = e.target.value;

                            if (formData.countryCode === "+1") {
                              value = formatUSPhone(value).slice(0, 15);
                            } else {
                              value = value.replace(/\D/g, "").slice(0, 10);
                            }
                            navigate(`/events/${eventDetails?._id}`);
                            // const isValid = await validateData(rsvpSchema, formData, setErrors);
                            // if (!isValid) return;

                            // try {
                            //     setSubmitting(true);
                            //     const obj = {
                            //         event_id: formData.event,
                            //         first_name: formData.full_name.split(" ")[0] || "",
                            //         last_name: formData.full_name.split(" ")[1] || "",
                            //         email: formData.email,
                            //         phone: formData.phone,
                            //         country_code: formData.countryCode,
                            //         total_persons: formData.number_of_guests,
                            //         user_id: user?._id || "",
                            //     }

                            //     console.log("Submitting RSVP with data:", obj);

                            //     const response = await API.post(api_paths.member.EVENTS.ADD_RSVP, obj);

                            //     console.log("RSVP Response:", response.data);

                            //     toastSuccessMessage(response?.data?.message);

                            //     setSubmitting(false);
                            //     navigate("/");

                            // } catch (error) {
                            //     toastErrorMessage("Failed to submit RSVP. Please try again.");
                            //     setSubmitting(false);
                            //     // console.error("Error submitting RSVP:", error);
                            // } finally {
                            //     setSubmitting(false);
                            // }

                            setFormData({ ...formData, phone: value });
                            errors.phone && clearError(setErrors, "phone");
                          }}
                          maxLength={formData.countryCode === "+1" ? 15 : 10}
                        />
                      </Col>
                    </Row>

                    {errors.phone && (
                      <Form.Text className="text-danger">
                        {errors.phone.message}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="formGuests">
                    <Form.Label>Number of Guests</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        value={formData.number_of_guests}
                        readOnly
                      />
                      <Button
                        className="increment-options"
                        onClick={handleDecrement}
                      >
                        -
                      </Button>
                      <Button
                        className="increment-options"
                        onClick={handleIncrement}
                      >
                        +
                      </Button>
                    </InputGroup>
                    {errors.number_of_guests && (
                      <Form.Text className="text-danger">
                        {errors.number_of_guests.message}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Button
                    type="submit"
                    className="btn  btn-orange-md px-4 border-0"
                    disabled={submitting}
                  >
                    Confirm RSVP
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* RIGHT: Details card */}
          <Col lg={6}>
            {eventDetailsLoading ? (
              <Card className="shadow-sm h-100">
                {/* Image skeleton */}
                <div
                  className="w-100 bg-light placeholder-glow"
                  style={{ height: "200px" }}
                >
                  <span className="placeholder w-100 h-100"></span>
                </div>

                <Card.Body>
                  {/* Title */}
                  <Placeholder
                    as={Card.Title}
                    animation="wave"
                    className="w-75"
                  >
                    <Placeholder xs={9} />
                  </Placeholder>

                  {/* Price */}
                  <Placeholder as={Card.Text} animation="wave" className="w-50">
                    <Placeholder xs={6} />
                  </Placeholder>

                  {/* Description */}
                  <Placeholder as={Card.Text} animation="wave">
                    <Placeholder xs={12} />
                    <Placeholder xs={10} />
                    <Placeholder xs={8} />
                  </Placeholder>

                  {/* Time */}
                  <Placeholder as={Card.Text} animation="wave" className="w-50">
                    <Placeholder xs={7} />
                  </Placeholder>

                  {/* Location */}
                  <Placeholder as={Card.Text} animation="wave" className="w-75">
                    <Placeholder xs={9} />
                  </Placeholder>
                </Card.Body>
              </Card>
            ) : eventDetails ? (
              <Card className="rsvp-card h-100">
                {eventDetails?.image_path && (
                  <Card.Img
                    variant="top"
                    className="w-100 rsvp-card-img"
                    src={`${IMG_PATH}${eventDetails?.image_path}`}
                  />
                )}
                <Card.Body>
                  <Card.Title className="fw-bold rsvp-detail-title">
                    {eventDetails?.title || "N/A"}
                  </Card.Title>
                  <Card.Text className="rsvp-detail-subtitle fw-medium f-16">
                    Tickets start from ${eventDetails?.price ?? "N/A"}
                  </Card.Text>
                  <Card.Text>
                    {eventDetails?.description || "No description available."}
                  </Card.Text>
                  <Card.Text className="d-flex align-items-center">
                    <FaRegClock size={16} />
                    <span className="ms-2">
                      <span>{formatEventDate(eventDetails)}</span>
                    </span>
                  </Card.Text>
                  <Card.Text>
                    <HiOutlineLocationMarker size={16} />
                    <span className="ms-2">{eventDetails?.venue || "N/A"}</span>
                  </Card.Text>
                </Card.Body>
              </Card>
            ) : (
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title className="fw-bold" style={{ paddingTop: "8px" }}>
                    Event Details
                  </Card.Title>

                  <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                    <Card.Text className="text-muted">
                      No details available for the selected event.
                    </Card.Text>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Rsvp;
